import type { Liquidacao } from '../../../services/liquidacoes.service';

/**
 * Extrato da liquidação para entregar ao PROMOTOR: todos os custos discriminados, em
 * linguagem de quem recebe. Abre numa janela própria para imprimir ou guardar em PDF.
 *
 * Nunca mostra o custo real da SISP nem a margem: isso é da Pagali (pedido da Linda,
 * 20/10). A taxa por cartão é a cobrada ao promotor.
 */

const NOME_CARTAO: Record<string, string> = {
  vint4: 'Vinti4', vinti4: 'Vinti4', visa: 'Visa', mcard: 'Mastercard', mastercard: 'Mastercard',
  amex: 'American Express',
};
const NOME_MEIO: Record<string, string> = {
  VINTI4: 'Vinti4', CARTAO_GUARDADO: 'Cartão guardado (Vinti4)', INTERNACIONAL: 'Cartões internacionais (Visa, Mastercard, Amex)',
};
const ORDEM = ['Vinti4', 'Cartão guardado (Vinti4)', 'Cartões internacionais (Visa, Mastercard, Amex)', 'Visa', 'Mastercard', 'American Express'];

const cve = (n: number) => `${Math.round(Number(n) || 0).toLocaleString('pt-PT')} $`;
const pct = (v: number) => `${v.toLocaleString('pt-PT', { maximumFractionDigits: 2 })}%`;
const dia = (s?: string | null) => (s ? new Date(s).toLocaleDateString('pt-PT', { timeZone: 'UTC' }) : '—');
const esc = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);

function detalhe(l: Liquidacao) {
  const d = typeof l.sisp_detail === 'string' ? JSON.parse(l.sisp_detail) : l.sisp_detail;
  // Por meio de pagamento (Vinti4, Cartão guardado, Internacional) quando a liquidação o tem;
  // as anteriores a 20/10 só têm por cartão.
  const porMeio = d?.porMeio && Object.keys(d.porMeio).length ? d.porMeio : null;
  const linhas = Object.entries(porMeio ?? d?.porCartao ?? {}).map(([k, c]: [string, any]) => {
    const nome = porMeio ? (NOME_MEIO[k] ?? k) : (NOME_CARTAO[k.toLowerCase()] ?? k);
    // Sem taxa no contrato o cobrado é o valor real: mostra-se a percentagem efectiva.
    const taxa = c.taxaBps != null ? c.taxaBps / 100 : (c.valorCVE ? (c.feeCVE / c.valorCVE) * 100 : 0);
    return { nome, trx: Number(c.trx) || 0, valor: Number(c.valorCVE) || 0, fee: Number(c.feeCVE) || 0, taxa };
  });
  linhas.sort((a, b) => (ORDEM.indexOf(a.nome) + 99 * +(ORDEM.indexOf(a.nome) < 0))
    - (ORDEM.indexOf(b.nome) + 99 * +(ORDEM.indexOf(b.nome) < 0)));
  return { linhas, semSisp: (d?.pedidosSemSisp ?? []).length as number };
}

export function abrirExtrato(l: Liquidacao, promotor: string): void {
  const bruto = Number(l.gross_ticketing_cve) || 0;
  const comissao = Number(l.commission_cve) || 0;
  const ivaCom = Number(l.vat_on_commission_cve) || 0;
  const sisp = Number(l.sisp_fee_cve) || 0;
  const ivaSisp = Number(l.sisp_fee_vat_cve) || 0;
  const liquido = Number(l.net_payable_cve) || 0;
  const taxaIva = comissao ? Math.round((ivaCom / comissao) * 100) : 15;
  const { linhas, semSisp } = detalhe(l);
  const bilhetes = Number(l.bilhetes) || 0;

  const linhasCartao = linhas.map((c) => `
    <tr class="sub"><td>${esc(c.nome)} — ${c.trx} pagamento${c.trx === 1 ? '' : 's'}, ${cve(c.valor)} × ${pct(c.taxa)}</td>
    <td class="v">− ${cve(c.fee)}</td></tr>`).join('');

  const html = `<!doctype html><html lang="pt"><head><meta charset="utf-8">
<title>Extrato de liquidação — ${esc(l.evento_titulo || promotor)}</title>
<style>
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1d1d1f;background:#fff;margin:0;padding:40px;max-width:780px}
  h1{font-size:22px;margin:0 0 4px;color:#5b2a86} .marca{font-weight:800;color:#5b2a86;letter-spacing:.5px}
  .cab{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #5b2a86;padding-bottom:16px;margin-bottom:24px}
  .meta{font-size:13px;color:#555;line-height:1.6} .meta b{color:#1d1d1f}
  table{width:100%;border-collapse:collapse;font-size:14px} td{padding:9px 4px;border-bottom:1px solid #eee}
  td.v{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
  tr.sub td{color:#555;font-size:13px;padding-left:22px;border-bottom:1px dashed #eee}
  tr.sec td{font-weight:600;padding-top:16px}
  tr.total td{font-size:17px;font-weight:700;border-top:2px solid #1d1d1f;border-bottom:none;padding-top:14px}
  .nota{margin-top:24px;font-size:12px;color:#666;line-height:1.6}
  .aviso{margin-top:16px;padding:10px 12px;background:#fff7e6;border:1px solid #f0c36d;border-radius:6px;font-size:12px}
  .imp{margin-top:28px} @media print{.imp{display:none} body{padding:0}}
  button{background:#5b2a86;color:#fff;border:0;border-radius:6px;padding:10px 18px;font-size:14px;cursor:pointer}
</style></head><body>
<div class="cab">
  <div><div class="marca">PAGALI</div><h1>Extrato de liquidação</h1></div>
  <div class="meta">
    <div>Promotor: <b>${esc(promotor)}</b></div>
    ${l.evento_titulo ? `<div>Evento: <b>${esc(l.evento_titulo)}</b></div>` : ''}
    <div>Vendas de ${dia(l.period_start)} a ${dia(l.period_end)}</div>
    <div>Estado: <b>${l.status === 'PAID' ? `Pago${l.bank_reference ? ` (ref. ${esc(l.bank_reference)})` : ''}` : 'Por pagar'}</b></div>
  </div>
</div>
<table>
  <tr class="sec"><td>Receita de bilheteira${bilhetes ? ` — ${bilhetes.toLocaleString('pt-PT')} bilhetes` : ''}</td><td class="v">${cve(bruto)}</td></tr>
  <tr><td>Comissão Pagali</td><td class="v">− ${cve(comissao)}</td></tr>
  <tr><td>IVA sobre a comissão (${taxaIva}%)</td><td class="v">− ${cve(ivaCom)}</td></tr>
  ${sisp > 0 ? `
  <tr class="sec"><td>Taxa de processamento dos pagamentos com cartão</td><td class="v">− ${cve(sisp)}</td></tr>
  ${linhasCartao}
  ${ivaSisp > 0 ? `<tr><td>IVA sobre o serviço Pagali (${taxaIva}%)</td><td class="v">− ${cve(ivaSisp)}</td></tr>` : ''}` : ''}
  <tr class="total"><td>Valor a transferir ao promotor</td><td class="v">${cve(liquido)}</td></tr>
</table>
${semSisp > 0 ? `<div class="aviso">${semSisp} pagamento${semSisp === 1 ? '' : 's'} ainda sem confirmação do processador:
a taxa de processamento desse${semSisp === 1 ? '' : 's'} pagamento${semSisp === 1 ? '' : 's'} não está incluída neste extrato.</div>` : ''}
<div class="nota">A comissão Pagali segue as condições do contrato em vigor. A taxa de processamento
corresponde aos pagamentos com cartão destas vendas, por tipo de cartão. Valores em escudos cabo-verdianos (CVE).</div>
<div class="imp"><button onclick="window.print()">Imprimir / guardar em PDF</button></div>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('O browser bloqueou a janela do extrato. Permita pop-ups para admin.pagali.cv.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
